// Globals
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { deleteProductVariation, getProductVariations, productsSelector } from '../../feature';

// Models
import { IProductVariation } from '@/models/products.model';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';

// Components
import {
  StyledHeaderBlock,
  StyledContainerTitle,
  StyledContainerWrapper,
  StyledContentBlock,
  StyledAddVariationButton,
  StyledPlusCircleIcon,
  StyledTitleModal,
  StyledSubTitleModal,
  StyledButtonsWrapper,
  StyledBackButton,
} from './ProductDetailsVariationsContainerStyles';
import BulkAddVariationsContainer from '../BulkAddVariationsContainer/BulkAddVariationsContainer';
import { Button } from '@/ui';
import { Modal, ModalSize, showSuccessToast } from '@/components';
import { ProductVariationsList } from '../../components';

// Helpers
import { useBoolean, useModal } from '@/hooks';

const ProductDetailsVariationsContainer = () => {
  const { t } = useTranslation(['products']);
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const { isOpened, openModal, closeModal } = useModal();
  const {
    value: isOpenAddVariationsModal,
    setFalse: closeAddVariationsModal,
    setTrue: openAddVariationsModal,
  } = useBoolean();
  const [variationToDeleteId, setVariationToDeleteId] = useState<string | null>(null);

  const { productVariations, isLoading } = useAppState(productsSelector);
  const { canManage } = usePermissions();
  const canManageProduct = canManage(Resource.PRODUCT);

  const handleDeleteVariation = async () => {
    try {
      if (variationToDeleteId) {
        await dispatch(deleteProductVariation(variationToDeleteId)).unwrap();
        showSuccessToast({ message: t('product_variations_delete_success') });
      }
      closeModal();
    } catch {}
  };

  const onDeleteButtonClick = (variation: IProductVariation) => {
    openModal();
    setVariationToDeleteId(variation.id);
  };

  useEffect(() => {
    if (id) {
      dispatch(getProductVariations(id));
    }
  }, [id]);

  return (
    <StyledContainerWrapper>
      <StyledHeaderBlock>
        <StyledContainerTitle>{t('product_variations_tab_title')}</StyledContainerTitle>
        {canManageProduct && (
          <StyledAddVariationButton onClick={openAddVariationsModal}>
            <StyledPlusCircleIcon />
            {t('product_details_add_variation_button')}
          </StyledAddVariationButton>
        )}
      </StyledHeaderBlock>
      <StyledContentBlock>
        <ProductVariationsList
          productsVariations={productVariations}
          isLoading={isLoading}
          handleDeleteVariation={onDeleteButtonClick}
        />
      </StyledContentBlock>
      <Modal visible={isOpened} onCancel={closeModal} size={ModalSize.LARGE}>
        <StyledTitleModal>{t('product_variations_delete_modal_title')}</StyledTitleModal>
        <StyledSubTitleModal>{t('product_variations_delete_modal_subtitle')}</StyledSubTitleModal>
        <StyledButtonsWrapper>
          <StyledBackButton onClick={closeModal}>{t('product_variations_delete_modal_button_back')}</StyledBackButton>
          <Button onClick={handleDeleteVariation} type="text" loading={isLoading}>
            {t('product_variations_delete_modal_button_delete')}
          </Button>
        </StyledButtonsWrapper>
      </Modal>
      {id && (
        <BulkAddVariationsContainer
          visible={isOpenAddVariationsModal}
          productId={id}
          existingVariations={productVariations || []}
          onClose={closeAddVariationsModal}
        />
      )}
    </StyledContainerWrapper>
  );
};

export default ProductDetailsVariationsContainer;
